import { ReactNode } from 'react';
import { useFeature } from '@/hooks/useFeature';

interface FeatureGuardProps {
  /** The name of the feature flag to check */
  name: string;
  /** Content to render when feature is enabled */
  children: ReactNode;
  /** Optional fallback to render when feature is disabled (defaults to null) */
  fallback?: ReactNode;
}

/**
 * Conditionally renders children based on feature flag status
 * 
 * @example
 * <FeatureGuard name="newDashboard">
 *   <NewDashboardButton />
 * </FeatureGuard>
 * 
 * @example with fallback
 * <FeatureGuard name="betaFeature" fallback={<ComingSoonBadge />}>
 *   <BetaFeaturePanel />
 * </FeatureGuard>
 */
export function FeatureGuard({ name, children, fallback = null }: FeatureGuardProps) {
  const isEnabled = useFeature(name);
  
  if (!isEnabled) {
    return <>{fallback}</>;
  }
  
  return <>{children}</>;
}
